// TT-291: read the visitor's OneTrust choice. Anything other than an explicit
// Targeting yes counts as no consent. Window and cookies are parameters so tests run without a browser.

export const MARKETING_CONSENT_GROUP = 'C0004';

const GROUPS_UPDATED_EVENT = 'OneTrustGroupsUpdated';

type ConsentWindow = Pick<EventTarget, 'addEventListener' | 'removeEventListener'> & {
  OnetrustActiveGroups?: unknown;
};

function currentWindow(): ConsentWindow | undefined {
  return typeof window === 'undefined' ? undefined : window;
}

function currentCookies(): string {
  try {
    return typeof document === 'undefined' ? '' : document.cookie;
  } catch {
    return '';
  }
}

function readCookie(cookies: string, name: string): string | undefined {
  const match = cookies.split(';').find((part) => part.trim().startsWith(`${name}=`));
  return match?.trim().slice(name.length + 1) || undefined;
}

// OnetrustActiveGroups is a comma list such as ",C0001,C0004,"
function activeGroupsAllow(groups: unknown): boolean | undefined {
  if (typeof groups !== 'string') {
    return undefined;
  }
  return groups.split(',').includes(MARKETING_CONSENT_GROUP);
}

// OptanonConsent holds groups=C0001:1,C0004:0 inside a URL encoded query string
function cookieAllows(cookies: string): boolean {
  try {
    const raw = readCookie(cookies, 'OptanonConsent');
    if (!raw) {
      return false;
    }
    const groups = new URLSearchParams(raw).get('groups') ?? '';
    return groups.split(',').includes(`${MARKETING_CONSENT_GROUP}:1`);
  } catch {
    return false;
  }
}

export function hasMarketingConsent(
  win: ConsentWindow | undefined = currentWindow(),
  cookies: string = currentCookies()
): boolean {
  try {
    return activeGroupsAllow(win?.OnetrustActiveGroups) ?? cookieAllows(cookies);
  } catch {
    return false;
  }
}

/** True once the visitor has answered the banner, either way. */
export function hasAnsweredConsent(cookies: string = currentCookies()): boolean {
  return Boolean(readCookie(cookies, 'OptanonAlertBoxClosed'));
}

/** Calls back with the Targeting choice on every OneTrust update; returns an unsubscribe. */
export function onConsentChange(
  cb: (marketing: boolean) => void,
  win: ConsentWindow | undefined = currentWindow()
): () => void {
  if (!win) {
    return () => {};
  }
  const listener = (event: Event) => {
    const { detail } = event as CustomEvent<unknown>;
    cb(Array.isArray(detail) ? detail.includes(MARKETING_CONSENT_GROUP) : hasMarketingConsent(win));
  };
  win.addEventListener(GROUPS_UPDATED_EVENT, listener);
  return () => win.removeEventListener(GROUPS_UPDATED_EVENT, listener);
}
